import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

/**
 * Expandable Feature Card Component
 * Click to expand and show detailed content for lead generation
 */
const FeatureCard = ({ icon, title, subtitle, details, benefits, isExpanded, onToggle }) => (
  <div
    className={`qai-feature-card ${isExpanded ? 'expanded' : ''}`}
    onClick={onToggle}
  >
    <div className="qai-card-header">
      <div className="qai-card-icon">{icon}</div>
      <div className="qai-card-titles">
        <div className="qai-card-title">{title}</div>
        <div className="qai-card-subtitle">{subtitle}</div>
      </div>
      <div className={`qai-card-arrow ${isExpanded ? 'rotated' : ''}`}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
    </div>

    <div className={`qai-card-content ${isExpanded ? 'show' : ''}`}>
      <p className="qai-card-details">{details}</p>
      <ul className="qai-card-benefits">
        {benefits.map((benefit, idx) => (
          <li key={idx}>
            <span className="benefit-check">✓</span>
            {benefit}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/**
 * FAQ Accordion Item Component
 */
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className={`faq-item ${isOpen ? 'open' : ''}`} onClick={onToggle}>
    <div className="faq-question">
      <span>{question}</span>
      <span className={`faq-arrow ${isOpen ? 'rotated' : ''}`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    </div>
    <div className={`faq-answer ${isOpen ? 'show' : ''}`}>
      {answer}
    </div>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();

  // Expandable cards state
  const [expandedCard, setExpandedCard] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  // Q.AI Feature cards data
  const qaiFeatures = [
    {
      id: 'journal',
      icon: '📊',
      title: 'Trade Journal',
      subtitle: 'Auto-import & analyze your MT5 trades',
      details: 'Connect your MetaTrader 5 terminal via QBridge. Your trades sync automatically in real-time. Q.AI identifies patterns, common mistakes, and your most profitable setups.',
      benefits: [
        'Real-time trade synchronization',
        'Pattern recognition & behavior analysis',
        'Detailed P&L breakdown by strategy',
        'Identify emotional trading patterns',
        'Track improvement over time'
      ]
    },
    {
      id: 'ea',
      icon: '🔬',
      title: 'EA Laboratory',
      subtitle: 'AI-powered Expert Advisor analysis',
      details: 'Upload your MQL5 Expert Advisors and let Q.AI audit your code. Get instant risk assessments, bug detection, and optimization suggestions. Safe saves with _QAI suffix - your original code is never modified.',
      benefits: [
        'Code quality & risk assessment',
        'Bug detection & fix suggestions',
        'Performance optimization tips',
        'Safe saves (original untouched)',
        'Side-by-side diff view'
      ]
    },
    {
      id: 'coach',
      icon: '🎯',
      title: 'AI Trading Coach',
      subtitle: '24/7 personalized trading guidance',
      details: 'Ask Q.AI anything about trading, your strategies, or market analysis. Get context-aware answers based on YOUR trading data. Basic questions are FREE - no commitment required.',
      benefits: [
        '24/7 availability',
        'Context-aware responses',
        'FREE tier available',
        'Learns from your trading style',
        'Multi-language support'
      ]
    }
  ];

  // FAQ data
  const faqData = [
    {
      question: 'What is QBridge and how does it work?',
      answer: 'QBridge is a secure Windows desktop application that connects your MetaTrader 5 terminal to Q.AI Coach. It runs locally on your computer, never accessing your MT5 password. It only reads your closed trade history (read-only access) and syncs it securely using AES-256 encryption.'
    },
    {
      question: 'Is my trading data safe and private?',
      answer: 'Absolutely. QBridge runs 100% locally on YOUR computer. We never see your passwords. Your trade data is encrypted with bank-grade AES-256 encryption. We do not share your data with any third parties. You can delete your data at any time.'
    },
    {
      question: 'Is Q.AI a signal provider or financial advisor?',
      answer: 'No. Q.AI is an EDUCATIONAL SOFTWARE TOOL designed to help you learn and improve. We do not provide trading signals, investment advice, or recommendations. Trading forex carries significant risk and you should carefully consider your investment objectives.'
    },
    {
      question: 'How much does Q.AI Coach cost?',
      answer: 'Q.AI uses a token-based system. You get FREE tokens when you sign up to try the service. Basic questions and QBridge setup help are always FREE. You can purchase additional tokens as needed - no subscriptions or hidden fees.'
    },
    {
      question: 'What platforms does QBridge support?',
      answer: 'QBridge currently supports Windows 10 and Windows 11. MetaTrader 5 (MT5) is required. We plan to add MacOS and MT4 support in future updates. You can still use Q.AI Chat on any device including mobile.'
    },
    {
      question: 'Can I use Q.AI without connecting MT5?',
      answer: 'Yes! You can use Q.AI Chat for general trading questions, education, and strategy discussions without connecting your MT5. QBridge is only needed for automatic trade import and personalized analysis based on your actual trading data.'
    }
  ];

  return (
    <div className="landing-root">
      {/* Header */}
      <header className="landing-header">
        <div className="landing-logo">
          <span className="rocket-emoji">🚀</span>
          <div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
              <span className="brand-title-row" style={{ fontWeight: 900, fontSize: 34, color: '#3deaff', letterSpacing: 0.5, lineHeight: 1, display: 'block' }}>
                  Q<span style={{ fontWeight: 700, fontSize: 35, color: '#3deaff', marginLeft: 0 }}>TradeView</span>
              </span>
              <span style={{ fontSize: 11, color: '#bfc9db', fontWeight: 700,fontStyle:'italic', textTransform:'uppercase',display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>
                  High-Frequency Trading Engine
                  <span style={{ color: '#FFD600', fontSize: 9, marginLeft: 2 }}>★</span>
              </span>
          </div>
</div>

        </div>
        <div className="landing-header-actions">
          <button className="landing-btn secondary" onClick={() => navigate('/login')}>Login</button>
          {/* <button className="landing-btn primary" onClick={() => navigate('/register')}>Get Started</button> */}
        </div>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        {/* <div className="hero-badge">⚡ Next-Generation Trading Platform</div> */}
        <h1 className="hero-title">Trade with <span className="brand-gradient">Precision</span></h1>
        <div className="hero-desc">
          Advanced algorithmic trading platform with real-time market data,<br />
          sophisticated charting tools, and institutional-grade execution capabilities.
        </div>
        <div className="hero-actions">
          <button className="landing-btn primary" onClick={() => navigate('/login')}>Start Trading Now →</button>
          <button className="landing-btn outline">▶ Watch Demo</button>
        </div>
        <div className="hero-stats">
          <div><span>50ms</span><br />Average Latency</div>
          <div><span>99.9%</span><br />Uptime</div>
          <div><span>24/7</span><br />Support</div>
        </div>
        <div className="hero-dashboard-mock">
          <img
            src="/laptops-graph.png"
            alt="Trading platform chart preview"
            className="laptop-graph-img"
          />
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-section">
        <h2>Why Choose QuantFIX?</h2>
        <div className="why-subtitle">Built for professional traders who demand speed, reliability, and advanced analytics</div>
        <div className="why-features">
          <div className="why-feature">
            <div className="why-icon">📈</div>
            <div>
              <div className="why-title">Advanced Analytics</div>
              <div className="why-desc">Real-time market data with sophisticated charting tools and technical indicators for informed decision making.</div>
            </div>
          </div>
          <div className="why-feature">
            <div className="why-icon">⚡</div>
            <div>
              <div className="why-title">Lightning Fast</div>
              <div className="why-desc">Ultra-low latency execution with sub-50ms order processing for competitive advantage in fast markets.</div>
            </div>
          </div>
          <div className="why-feature">
            <div className="why-icon">🛡️</div>
            <div>
              <div className="why-title">Bank-Grade Security</div>
              <div className="why-desc">Enterprise-level security with end-to-end encryption and multi-factor authentication for your peace of mind.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stat"><span>$2.5T+</span><br />Daily Volume</div>
        <div className="stat"><span>150+</span><br />Markets</div>
        <div className="stat"><span>500K+</span><br />Active Traders</div>
        <div className="stat"><span>15+</span><br />Years Experience</div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          Q.AI TRADING COACH SECTION - Premium Feature Highlight
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="qai-section">
        <div className="qai-badge">🤖 AI-POWERED</div>
        <h2 className="qai-title">
          Q.AI Trading Coach
        </h2>
        <p className="qai-subtitle">
          Your Personal AI-Powered Trading Intelligence
        </p>

        {/* Expandable Feature Cards */}
        <div className="qai-features-grid">
          {qaiFeatures.map((feature) => (
            <FeatureCard
              key={feature.id}
              icon={feature.icon}
              title={feature.title}
              subtitle={feature.subtitle}
              details={feature.details}
              benefits={feature.benefits}
              isExpanded={expandedCard === feature.id}
              onToggle={() => setExpandedCard(expandedCard === feature.id ? null : feature.id)}
            />
          ))}
        </div>

        {/* CTA Button */}
        <div className="qai-cta-container">
          <button
            className="qai-cta-button"
            onClick={() => navigate('/login')}
          >
            🚀 Try Q.AI Coach FREE
          </button>
          <p className="qai-cta-note">No credit card required • Free tokens included</p>
        </div>

        {/* Security Badge */}
        <div className="qai-security-badge">
          <span className="security-icon">🔒</span>
          <span>Your data stays private. QBridge runs 100% locally on your PC. Bank-grade AES-256 encryption.</span>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FAQ SECTION
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="faq-section">
        <h2 className="faq-title">Frequently Asked Questions</h2>
        <p className="faq-subtitle">Everything you need to know about Q.AI Coach</p>

        <div className="faq-container">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFAQ === index}
              onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
            />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="faq-contact">
          <p>Still have questions?</p>
          <a href="mailto:info@qforgex.com" className="faq-contact-link">
            Contact us at info@qforgex.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="rocket-emoji">🚀</span>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>
              <span className="brand-title-row" style={{ fontWeight: 900, fontSize: 34, color: '#3deaff', letterSpacing: 0.5, lineHeight: 1, display: 'block' }}>
                  Q<span style={{ fontWeight: 700, fontSize: 35, color: '#3deaff', marginLeft: 0 }}>TradeView</span>
              </span>
              <span style={{ fontSize: 11, color: '#bfc9db', fontWeight: 700,fontStyle:'italic', textTransform:'uppercase',display: 'flex', alignItems: 'center', lineHeight: 1.2 }}>
                  High-Frequency Trading Engine
                  <span style={{ color: '#FFD600', fontSize: 9, marginLeft: 2 }}>★</span>
              </span>
          </div>


          </div>
          <div className="footer-links">
            <a href="#" className="footer-link">Home</a>
            <a href="#" className="footer-link">Features</a>
            <a href="#" className="footer-link">Pricing</a>
            <a href="#" className="footer-link">Contact</a>
          </div>
        </div>
        <div className="footer-bottom">
          © {new Date().getFullYear()} Q TradeView. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 